// React Query hooks for pricing config.
// Exported via "@repo/ui/pricing-hooks" sub-path — NOT included in the main barrel
// because @tanstack/react-query is an optional peer dep that not all apps install.

import { useEffect } from "react";

import { getUserIdFromRefreshToken, LogSentryException } from "@repo/core-utils";
import { useQuery } from "@tanstack/react-query";

import type { PriceConfig, PriceConfigResponseData, TierPriceConfig } from "./priceConfigTypes";
import { priceConfigQueryKey, transformPriceConfig } from "./priceConfigTypes";

export type { PriceConfig, PriceConfigResponseData, TierPriceConfig };
export {
  CurrencySymbols,
  priceConfigQueryKey,
  SubscriptionPeriod,
  SubscriptionTier,
  transformPriceConfig,
} from "./priceConfigTypes";

export type OnPriceShownCallback = (params: {
  currencyCode: string;
  yearlyPrice: number;
  monthlyPrice: number;
}) => void;

export const getPriceConfig = async ({
  apiBaseUrl,
  userId,
  signal,
}: {
  apiBaseUrl: string;
  userId: string | null;
  signal?: AbortSignal;
}): Promise<PriceConfigResponseData> => {
  const searchParams = new URLSearchParams();
  if (userId) {
    searchParams.append("user_id", userId);
  }
  const url = `${apiBaseUrl}/plans/web-prices?${searchParams.toString()}`;

  const response = await fetch(url, { signal }).catch((error: unknown) => {
    LogSentryException({ messagePrefix: "Price config error", error });
    throw error;
  });

  if (!response.ok) {
    const error = new Error(`Price config request failed: ${response.status}`);
    LogSentryException({ messagePrefix: "Price config error", error });
    throw error;
  }

  return response.json();
};

export function usePriceConfig({
  apiBaseUrl,
  onPriceShown,
}: {
  apiBaseUrl: string;
  onPriceShown?: OnPriceShownCallback;
}) {
  const userId = getUserIdFromRefreshToken();
  const queryKey = [...priceConfigQueryKey, userId ?? "anon"];

  const query = useQuery<PriceConfigResponseData, Error, PriceConfig>({
    queryKey,
    queryFn: ({ signal }) => getPriceConfig({ apiBaseUrl, userId, signal }),
    staleTime: Infinity,
    select: transformPriceConfig,
  });

  const priceConfigData = query.data;
  useEffect(() => {
    if (priceConfigData && onPriceShown) {
      const firstTier = Object.values(priceConfigData.tiers)[0];
      if (firstTier) {
        onPriceShown({
          currencyCode: priceConfigData.currencyCode,
          yearlyPrice: firstTier.yearly,
          monthlyPrice: firstTier.monthly,
        });
      }
    }
  }, [priceConfigData, onPriceShown]);

  return query;
}
